import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { isAxiosError } from 'axios'
import { useQuery } from '@tanstack/react-query'
import { Layout } from '../components/layout/Layout'
import { AlbumView } from '../components/album/AlbumView'
import { publicApiClient, getShareToken, setShareToken, clearShareToken } from '../api/client'
import type { Album } from '../types/album'
import { Lock, Loader2 } from 'lucide-react'

export const ShareAlbum = () => {
  const { id } = useParams<{ id: string }>()
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [token, setToken] = useState<string | null>(() => (id ? getShareToken(id) : null))
  const [isVerifying, setIsVerifying] = useState(false)

  const { data: album, isLoading, error: queryError } = useQuery({
    queryKey: ['sharedAlbum', id, token],
    queryFn: async () => {
      if (!id || !token) return null
      const response = await publicApiClient.get(`/albums/${id}`, {
        headers: { 'X-Share-Token': token }
      })
      const data = response.data.data
      // Token is expired/invalid — return null to trigger password prompt
      if (data.locked) {
        return null
      }
      return data as Album
    },
    enabled: !!id && !!token,
    retry: false,
  })

  useEffect(() => {
    if (!id) return

    // Case 1: Token was sent but album came back locked (discoverable, 200 OK)
    // or query returned null despite having a token
    if (token && !isLoading && !album && !queryError) {
      clearShareToken(id)
      setToken(null)
      return
    }

    // Case 2: Query errored — only clear token for auth-related errors
    if (queryError && isAxiosError(queryError)) {
      const status = queryError.response?.status
      if (status === 401 || status === 403 || status === 404) {
        clearShareToken(id)
        setToken(null)
      }
    }
  }, [queryError, id, isLoading, album, token])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!id || !password) return

    setIsVerifying(true)
    setError('')

    try {
      const response = await publicApiClient.post(`/share/${id}/verify`, { password })
      const accessToken = response.data.data.accessToken
      setShareToken(id, accessToken)
      setToken(accessToken)
    } catch (err) {
      setError('Incorrect password')
    } finally {
      setIsVerifying(false)
    }
  }

  if (isLoading) {
    return (
      <Layout>
        <div className="flex justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </Layout>
    )
  }

  if (!token || !album) {
    return (
      <Layout>
        <div className="max-w-md mx-auto py-20">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Enter password to view album</h1>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              autoFocus
            />
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button
              type="submit"
              disabled={isVerifying || !password}
              className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {isVerifying ? 'Verifying...' : 'View Album'}
            </button>
          </form>
        </div>
      </Layout>
    )
  }

  return (
    <Layout title={album.name}>
      <AlbumView album={album} albumId={id!} shareToken={token} />
    </Layout>
  )
}
